import { Component, OnInit, AfterViewInit, inject } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { filter, take, tap } from "rxjs/operators";
import { Store } from "@ngrx/store";

import { IcOrganisation } from "@intocare-medapp/organisation/ic-organisation";
import { IcEnvironment } from "@intocare-medapp/environment";

import { AuthModel } from "../shared";
import { SimpleToastService, SplashScreenService } from "../shared/modules";
import { selectIsAppVersionOutdated, selectLatestAppVersion } from "../shared/state/selectors/app-details.selectors";
import { SetupActions } from "./state/actions/setup.actions";

const noSpecialCharactersPattern = /^[a-zA-Z0-9.]+$/;
const validHomePattern = /^[a-z0-9]+\.[a-z0-9]+$/;

@Component({
	templateUrl: "setup.component.html",
	styleUrls: ["setup.component.scss"],
	standalone: false,
})
export class SetupComponent implements OnInit, AfterViewInit {
	private readonly icOrganisation = inject(IcOrganisation);
	private readonly fb = inject(UntypedFormBuilder);
	private readonly router = inject(Router);
	private readonly toastService = inject(SimpleToastService);
	private readonly splashscreenService = inject(SplashScreenService);
	private readonly translateService = inject(TranslateService);
	private readonly auth = inject(AuthModel);
	private readonly icEnvironment = inject(IcEnvironment);
	private readonly store = inject(Store);

	private environment = this.icEnvironment.getEnvironment();

	appVersion = this.environment.APP_VERSION;

	private loadingSubject$ = new BehaviorSubject(false);
	loading$ = this.loadingSubject$.asObservable();
	// @ts-expect-error TS2564
	form: UntypedFormGroup;
	invalidOrganisation = false;

	protected isAppVersionOutdated$ = this.store.select(selectIsAppVersionOutdated(this.environment.APP_VERSION));

	get hasOrganisationError() {
		const control = this.form.get("organisation");

		// @ts-expect-error TS18047
		return control.touched && (control.invalid || this.invalidOrganisation);
	}

	get organisationErrorLabel() {
		const control = this.form.get("organisation");

		// @ts-expect-error TS18047
		if (control.hasError("required")) {
			return "auth.validators.field_required";
		}

		// @ts-expect-error TS18047
		if (control.hasError("invalid_organisation_trigram_with_special_characters")) {
			return "auth.validators.organisation_invalid_trigram_with_special_characters";
		}

		// @ts-expect-error TS18047
		if (control.hasError("invalid_organisation_trigram")) {
			return "auth.validators.organisation_invalid_trigram";
		}

		if (this.invalidOrganisation) {
			return "auth.errors.unrecognized_organisation";
		}

		return "";
	}

	setup({ organisation }: { organisation: string }) {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const org = organisation.split(" ").join("").toLowerCase();

		this.loadingSubject$.next(true);
		this.auth
			.bootstrapUserPool(org, { forceRefresh: true })
			.pipe(
				tap(() => {
					this.icOrganisation.setOrganisation(org);
					// @ts-expect-error TS2531
					this.form.get("organisation").setErrors(undefined);
					this.invalidOrganisation = false;
				}),
			)
			.subscribe({
				next: (config) => {
					if (config.hostedUiUri) {
						this.router.navigate(["auth/sign-in"]);
					} else {
						this.router.navigate(["auth/sign-in-with-username"]);
					}
				},
				error: (err) => {
					if (err.status === 404) {
						this.invalidOrganisation = true;
					} else {
						this.toastService.error(this.translateService.instant("auth.errors.unknown"));
					}

					this.loadingSubject$.next(false);
				},
				complete: () => {
					this.loadingSubject$.next(false);
				},
			});
	}

	validateRegex(customError: string, pattern: RegExp): ValidatorFn {
		// @ts-expect-error TS2322
		return (control: UntypedFormControl) => {
			if (control.value && !pattern.test(control.value)) {
				return { [customError]: true };
			}

			return null;
		};
	}

	ngAfterViewInit() {
		this.splashscreenService.hide();
	}

	ngOnInit() {
		this.form = this.fb.group({
			organisation: [
				"",
				[
					Validators.required,
					this.validateRegex("invalid_organisation_trigram_with_special_characters", noSpecialCharactersPattern),
					this.validateRegex("invalid_organisation_trigram", validHomePattern),
				],
			],
		});
		const organisation = this.icOrganisation.getOrganisation();
		// @ts-expect-error TS2531
		this.form.get("organisation").setValue(organisation);

		// @ts-expect-error TS2531
		this.form.get("organisation").valueChanges.subscribe(() => {
			this.invalidOrganisation = false;
		});

		this.store
			.select(selectLatestAppVersion)
			.pipe(
				take(1),
				filter((version) => !version),
				tap(() => this.store.dispatch(SetupActions.getLatestAppVersion())),
			)
			.subscribe();
	}
}
